import { type DocumentSummary, type TierDetails } from "~/api/types";

type LayoutMode = "graph" | "grid";

const sortDocuments = (a: DocumentSummary, b: DocumentSummary) => {
  const aOrder = a.chartOrder;
  const bOrder = b.chartOrder;

  if (aOrder !== null && bOrder !== null) {
    return aOrder - bOrder;
  }

  // Documents with a chartOrder come before those without
  if (aOrder !== null) return -1;
  if (bOrder !== null) return 1;

  return a.latestVersion.title.localeCompare(b.latestVersion.title);
};

type DocumentId = number;
type TierId = number;
type DocumentMap = Map<DocumentId, DocumentSummary>;
export type ChartColumn = Map<TierId, DocumentSummary[]>;

export type ChartData =
  | { mode: "grid"; column: ChartColumn }
  | { mode: "graph"; columns: ChartColumn[] };

const groupByTier = (documents: Iterable<DocumentSummary>): ChartColumn => {
  const column: ChartColumn = new Map();
  for (const document of documents) {
    if (!document.tier) continue;
    const tierDocuments = column.get(document.tier.id) ?? [];
    tierDocuments.push(document);
    column.set(document.tier.id, tierDocuments);
  }
  column.forEach((tierVersions) => tierVersions.sort(sortDocuments));
  return column;
};

export const getChartData = (
  documents: DocumentSummary[],
  tiers: TierDetails[],
  mode: LayoutMode,
): ChartData => {
  if (documents.length === 0) {
    return mode === "grid"
      ? { mode: "grid", column: new Map() }
      : { mode: "graph", columns: [] };
  }

  const documentMap: DocumentMap = new Map(
    documents.map((document) => [document.id, { ...document }]),
  );

  documentMap.forEach((document) => {
    documentMap.set(document.id, {
      ...document,
      parentDocumentIds: document.parentDocumentIds.filter((id) =>
        documentMap.has(id),
      ),
      childDocumentIds: document.childDocumentIds.filter((id) =>
        documentMap.has(id),
      ),
    });
  });

  if (mode === "grid") {
    return { mode: "grid", column: groupByTier(documentMap.values()) };
  }

  return {
    mode: "graph",
    columns: buildGraphLayout(documentMap, tiers),
  };
};

const buildGraphLayout = (
  documentMap: DocumentMap,
  tiers: TierDetails[],
): ChartColumn[] => {
  const graphs = createGraphs(documentMap);

  graphs.sort((a, b) => b.documents.length - a.documents.length);

  const tierIds = tiers.map((t) => t.id);
  const columns: ChartColumn[] = graphs.map((graph) =>
    layoutGraphWithBarycenter(graph, tierIds),
  );

  const placedIds = new Set<DocumentId>();
  graphs.forEach((g) =>
    g.documents.forEach((document) => placedIds.add(document.id)),
  );

  const trailingVersions = Array.from(documentMap.values()).filter(
    (document) => !placedIds.has(document.id),
  );
  const trailingColumn = groupByTier(trailingVersions);
  if (trailingColumn.size > 0) columns.push(trailingColumn);

  return columns;
};

const layoutGraphWithBarycenter = (
  graph: Graph,
  tierIds: TierId[],
): ChartColumn => {
  const column = groupByTier(graph.documents);

  const presentTierIds = tierIds.filter(
    (id) => (column.get(id)?.length ?? 0) > 0,
  );

  const ITERATIONS = 24;
  for (let iter = 0; iter < ITERATIONS; iter++) {
    let changed = false;
    if (sweep(column, presentTierIds, "down")) changed = true;
    if (sweep(column, presentTierIds, "up")) changed = true;
    if (!changed) break;
  }

  return column;
};

const sweep = (
  column: ChartColumn,
  tierIds: TierId[],
  direction: "down" | "up",
): boolean => {
  let changed = false;
  const start = direction === "down" ? 1 : tierIds.length - 2;
  const end = direction === "down" ? tierIds.length : -1;
  const step = direction === "down" ? 1 : -1;

  for (let i = start; i !== end; i += step) {
    const referenceTierId =
      direction === "down" ? tierIds[i - 1] : tierIds[i + 1];
    const currentTierId = tierIds[i];
    if (referenceTierId === undefined || currentTierId === undefined) continue;
    const referenceDocuments = column.get(referenceTierId) ?? [];
    const currentDocuments = column.get(currentTierId);
    if (!currentDocuments || currentDocuments.length <= 1) continue;

    const referencePos = new Map<DocumentId, number>();
    referenceDocuments.forEach((document, idx) =>
      referencePos.set(document.id, idx),
    );

    const reordered = orderByBarycenter(
      currentDocuments,
      referencePos,
      direction,
    );
    const orderChanged = reordered.some(
      (document, idx) => document.id !== currentDocuments[idx]!.id,
    );
    if (orderChanged) {
      column.set(currentTierId, reordered);
      changed = true;
    }
  }
  return changed;
};

const orderByBarycenter = (
  list: DocumentSummary[],
  referencePos: Map<DocumentId, number>,
  direction: "down" | "up",
): DocumentSummary[] => {
  const decorated = list.map((document, originalIdx) => {
    const neighborIds =
      direction === "down"
        ? document.parentDocumentIds
        : document.childDocumentIds;
    const positions = neighborIds
      .map((id) => referencePos.get(id))
      .filter((position): position is number => position !== undefined);
    const barycenter =
      positions.length === 0
        ? originalIdx
        : positions.reduce((a, b) => a + b, 0) / positions.length;
    return { document, barycenter, originalIdx };
  });
  decorated.sort(
    (a, b) => a.barycenter - b.barycenter || a.originalIdx - b.originalIdx,
  );
  return decorated.map((d) => d.document);
};

interface Graph {
  documents: DocumentSummary[];
}

const createGraphs = (documentMap: DocumentMap): Graph[] => {
  const graphs: Graph[] = [];
  const documentGraphMap = new Map<DocumentId, Graph>();

  documentMap.forEach((parentDocument) => {
    const parentDocumentId = parentDocument.id;
    for (const childDocumentId of parentDocument.childDocumentIds) {
      const childDocument = documentMap.get(childDocumentId);
      if (!childDocument) {
        // this could happen if a link was created for a draft document
        continue;
      }

      const parentGraph = documentGraphMap.get(parentDocumentId);
      const childGraph = documentGraphMap.get(childDocumentId);

      if (!parentGraph && !childGraph) {
        const graph: Graph = {
          documents: [parentDocument, childDocument],
        };
        graphs.push(graph);
        documentGraphMap.set(parentDocumentId, graph);
        documentGraphMap.set(childDocumentId, graph);
      } else if (parentGraph && !childGraph) {
        parentGraph.documents.push(childDocument);
        documentGraphMap.set(childDocumentId, parentGraph);
      } else if (!parentGraph && childGraph) {
        childGraph.documents.push(parentDocument);
        documentGraphMap.set(parentDocumentId, childGraph);
      } else if (parentGraph && childGraph && parentGraph !== childGraph) {
        for (const document of childGraph.documents) {
          documentGraphMap.set(document.id, parentGraph);
        }
        parentGraph.documents.push(...childGraph.documents);
        graphs.splice(graphs.indexOf(childGraph), 1);
      }
    }
  });

  return graphs;
};
